import { monotonicFactory } from 'ulid';

/**
 * Monotonic ULID factory — guarantees lexicographically sortable output even
 * when multiple `ulid()` calls land in the same millisecond. Adopted in
 * Phase 3 W3-T3 task #30 (DECISION-W3-11) for timestamp ordering + DB index
 * friendliness (BE IdempotencyRecord row inserts inherit time-sorted keys).
 *
 * Replaces the previous `import { ulid } from 'ulid'` call — same 26-char
 * Crockford Base32 output, backward compatible with BE 16-64 length
 * validation (no regex enforcement).
 */
const ulid = monotonicFactory();

/**
 * Idempotency-Key resolver per DEC-006 + RFC-001 §4.3 step 2 + architect H2.
 *
 * Scope: a single mutation lifetime. Generates ONE ULID on first call within
 * a mutation; retries reuse it; success or terminal error drops the entry.
 *
 * Keyed by a synchronous pre-fetch hash of (method, path, JSON.stringify(body))
 * so that TanStack Query retries — which re-invoke `mutationFn` with the same
 * args — transparently reuse the same ULID without React-state awareness.
 *
 * TTL: 10 min ceiling (must be < BE replay window — open question Q1).
 */
const TEN_MIN_MS = 10 * 60 * 1000;

type Entry = { key: string; createdAt: number };

const store = new Map<string, Entry>();

const hashBody = (body: unknown): string => {
  if (body == null) return '';
  if (typeof body === 'string') return body;
  return JSON.stringify(body);
};

/**
 * Fast FNV-1a 32-bit hash. Not cryptographic; sufficient for collision-rare
 * keying of (method + path + body) within a single page lifetime.
 */
const fnv1a = (s: string): string => {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = (h + ((h << 1) + (h << 4) + (h << 7) + (h << 8) + (h << 24))) >>> 0;
  }
  return h.toString(16).padStart(8, '0');
};

export const idempotencyKeyFor = (
  method: string,
  path: string,
  body: unknown,
): string => {
  const cacheKey = `${method.toUpperCase()} ${path} ${fnv1a(hashBody(body))}`;
  const existing = store.get(cacheKey);
  const now = Date.now();

  if (existing && now - existing.createdAt < TEN_MIN_MS) {
    return existing.key;
  }

  const key = ulid();
  store.set(cacheKey, { key, createdAt: now });
  return key;
};

export const releaseIdempotencyKey = (
  method: string,
  path: string,
  body: unknown,
): void => {
  const cacheKey = `${method.toUpperCase()} ${path} ${fnv1a(hashBody(body))}`;
  store.delete(cacheKey);
};

/** Test-only: clear all entries. */
export const __resetIdempotencyStore = (): void => {
  store.clear();
};
